import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { UserRound } from "lucide-react";
import styles from "@/components/sheet/LembarVisual.module.css";
import attentionStyles from "@/components/shell/AttentionVisual.module.css";
import decisionStyles from "@/components/shell/DecisionListVisual.module.css";
import workspaceStyles from "@/components/workspace/DecisionWorkspaceVisual.module.css";
import libraryStyles from "@/components/library/LibraryVisual.module.css";
import bookStyles from "@/components/library/BookDetailVisual.module.css";
import readerStyles from "@/components/library/ReaderVisual.module.css";
import notesStyles from "@/components/library/NotesVisual.module.css";
import accountStyles from "@/components/account/AccountVisual.module.css";
import { Ambience } from "@/components/ax/Ambience";
import { Sidebar } from "@/components/shell/Sidebar";
import { MobileHeader, TabBar } from "@/components/shell/MobileChrome";
import { NAV } from "@/components/shell/nav";
import { SubscriptionBanner } from "@/components/shell/SubscriptionBanner";
import { useI18n } from "@/i18n";
import { getSidebarCollapsed, setSidebarCollapsed } from "@/lib/prefs";

const TAB_ROOTS = new Set(NAV.map((n) => n.to));
const READER = /^\/library\/[^/]+\/bab\/[^/]+$/;
const BRIEF = /^\/keputusan\/(?!baru$)[^/]+$/;
const clean = (p) => p.replace(/\/+$/, "") || "/";

export const AppShell = () => {
  const { t } = useI18n();
  const [collapsed, setCollapsed] = useState(getSidebarCollapsed);
  const main = useRef(null);
  const { pathname } = useLocation();
  const prev = useRef(pathname);
  const [entry, setEntry] = useState({ path: null, done: true });

  // Masuk layar: hanya layar non-tab dan bukan reader. Pindah tab 0 ms.
  if (entry.path !== pathname) setEntry({ path: pathname, done: false });
  const path = clean(pathname);
  const attention = path === "/";
  const decisions = path === "/keputusan";
  const library = path === "/library";
  const libraryNotes = path === "/library/catatan";
  const account = path === "/akun";
  const bookDetail = /^\/library\/(?!catatan$)[^/]+$/.test(path);
  const reader = READER.test(path);
  const decisionDetail = /^\/keputusan\/(?!(?:baru|contoh)$)[^/]+$/.test(path);
  const decisionCommit = /^\/keputusan\/[^/]+\/commit$/.test(path);
  const sheetEditor = /^\/lembar\/[^/]+$/.test(path);
  const stitch = attention || decisions || decisionDetail || decisionCommit || library || libraryNotes || bookDetail || reader || account || path === "/lembar" || sheetEditor;
  const entering = !entry.done && !TAB_ROOTS.has(path) && !READER.test(path);
  // Reader dan Decision Brief: kertas bersih, tanpa wash dan sumbu.
  const paper = READER.test(path) || BRIEF.test(path);

  useEffect(() => {
    const id = setTimeout(() => setEntry((e) => ({ ...e, done: true })), 160);
    return () => clearTimeout(id);
  }, [pathname]);

  useEffect(() => {
    if (prev.current === pathname) return;
    prev.current = pathname;
    main.current?.focus({ preventScroll: true });
    window.scrollTo(0, 0);
  }, [pathname]);

  const toggle = () => {
    setSidebarCollapsed(!collapsed);
    setCollapsed(!collapsed);
  };

  return (
    <div className={`relative isolate min-h-screen bg-ax-bg text-ax-text ${stitch ? styles.workspace : ""} ${attention ? attentionStyles.workspace : ""} ${decisions ? decisionStyles.workspace : ""} ${decisionDetail || decisionCommit ? workspaceStyles.workspace : ""} ${library || libraryNotes || bookDetail || reader ? libraryStyles.workspace : ""} ${bookDetail ? bookStyles.workspace : ""} ${reader ? readerStyles.workspace : ""} ${libraryNotes ? notesStyles.workspace : ""} ${account ? accountStyles.workspace : ""}`}>
      {!paper && !stitch && <Ambience />}
      <a href="#konten" className="skip-link" data-testid="skip-link">
        {t("skip")}
      </a>
      <Sidebar collapsed={collapsed} onToggle={toggle} stitch={stitch} />
      <div className={`md:pl-[72px] ${collapsed ? "lg:pl-[72px]" : "lg:pl-[232px]"} ${stitch ? `${styles.body} ${collapsed ? styles.collapsedBody : ""}` : ""}`}>
        <MobileHeader stitch={stitch} />
        {stitch && <header className={styles.desktopHeader}>
          <span className={styles.headerLabel}>{t("sheet.listWorkspace")}</span>
          <Link to="/akun" aria-label={t("nav.account")} className={styles.accountLink}><UserRound size={18} strokeWidth={1.5} aria-hidden="true" /></Link>
        </header>}
        <SubscriptionBanner />
        <main
          id="konten"
          ref={main}
          tabIndex={-1}
          data-testid="app-main"
          className="px-4 pb-[calc(56px+var(--safe-bottom)+96px)] pt-8 outline-none md:px-6 md:pb-16 lg:px-8"
        >
          <div className={`mx-auto w-full max-w-[760px]${entering ? " screen-enter" : ""} ${stitch ? styles.content : ""} ${sheetEditor ? styles.editorContent : ""} ${attention ? attentionStyles.content : ""} ${reader ? readerStyles.content : ""} ${account ? accountStyles.content : ""}`}>
            <Outlet />
          </div>
          <p className="m-0 mt-10 text-center text-sm leading-5 text-ax-muted">
            <a href="mailto:halo@axoris.id" data-testid="support-email" className="text-ax-link hover:underline">
              halo@axoris.id
            </a>
          </p>
        </main>
      </div>
      <TabBar />
    </div>
  );
};
